// Shared validation utility for patient forms
// Handles comprehensive validation and real-time formatting

export interface ValidationErrors {
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  date_of_birth?: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_postalCode?: string;
}

export interface FormData {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  date_of_birth: string;
  gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  address_street: string;
  address_city: string;
  address_state: string;
  address_postalCode: string;
}

// Phone number formatting utilities
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters except for the case where user is typing
  const digits = value.replace(/\D/g, '');
  
  // Handle different input scenarios
  let phoneDigits = digits;
  
  // If starts with 1 and has 11 digits, assume it's a US number with country code
  if (phoneDigits.startsWith('1') && phoneDigits.length === 11) {
    phoneDigits = phoneDigits.substring(1);
  }
  
  // Limit to 10 digits max for US phone numbers
  phoneDigits = phoneDigits.substring(0, 10);
  
  // Format based on length - progressive formatting for better UX
  if (phoneDigits.length === 0) {
    return '';
  } else if (phoneDigits.length <= 3) {
    return phoneDigits; // Allow unformatted typing for area code
  } else if (phoneDigits.length <= 6) {
    return `(${phoneDigits.substring(0, 3)}) ${phoneDigits.substring(3)}`;
  } else if (phoneDigits.length <= 10) {
    return `(${phoneDigits.substring(0, 3)}) ${phoneDigits.substring(3, 6)}-${phoneDigits.substring(6)}`;
  }
  
  return phoneDigits; // Fallback to just digits if something goes wrong
};

export const getPhoneDigits = (formattedPhone: string): string => {
  // Extract just the digits from formatted phone
  const digits = formattedPhone.replace(/\D/g, '');
  // Remove leading 1 if present (country code)
  if (digits.startsWith('1') && digits.length === 11) {
    return digits.substring(1);
  }
  return digits;
};

export const isValidPhoneNumber = (phone: string): boolean => {
  const digits = getPhoneDigits(phone);
  return digits.length === 10;
};

// Zip code formatting utilities
export const formatZipCode = (value: string): string => {
  // Remove any non-digit characters except hyphen
  let cleaned = value.replace(/[^\d-]/g, '');
  
  // Format as XXXXX or XXXXX-XXXX
  if (cleaned.length <= 5) {
    // Just the 5-digit part
    cleaned = cleaned.replace(/\D/g, '');
  } else if (cleaned.length <= 10) {
    // Add hyphen after 5 digits
    cleaned = cleaned.replace(/(\d{5})(\d{1,4})/, '$1-$2');
  } else {
    // Limit to 10 characters max (XXXXX-XXXX)
    cleaned = cleaned.substring(0, 10);
    cleaned = cleaned.replace(/(\d{5})(\d{1,4})/, '$1-$2');
  }
  
  return cleaned;
};

export const isValidZipCode = (zip: string): boolean => {
  return /^\d{5}(-\d{4})?$/.test(zip);
};

// State formatting utility
export const formatState = (value: string): string => {
  // Only allow letters, convert to uppercase, limit to 2 characters
  return value.replace(/[^A-Za-z]/g, '').toUpperCase().substring(0, 2);
};

export const isValidState = (state: string): boolean => {
  return /^[A-Z]{2}$/.test(state);
};

// Name validation utilities
export const isValidName = (name: string): boolean => {
  // Allow letters, spaces, hyphens, and apostrophes only
  return /^[A-Za-z\s\-']+$/.test(name) && name.trim().length > 0;
};

export const isValidCity = (city: string): boolean => {
  // Allow letters, spaces, hyphens, periods, and apostrophes
  return /^[A-Za-z\s\-.']+$/.test(city) && city.trim().length > 0;
};

// Email validation utility
export const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Date validation utility
export const isValidDateOfBirth = (dateString: string): { isValid: boolean; error?: string } => {
  if (!dateString) {
    return { isValid: false, error: 'Date of birth is required' };
  }

  const today = new Date();
  const birthDate = new Date(dateString);
  const age = today.getFullYear() - birthDate.getFullYear();
  
  if (isNaN(birthDate.getTime())) {
    return { isValid: false, error: 'Please enter a valid date' };
  }
  
  if (birthDate > today) {
    return { isValid: false, error: 'Date of birth cannot be in the future' };
  }
  
  if (age > 150) {
    return { isValid: false, error: 'Please enter a valid date of birth' };
  }
  
  return { isValid: true };
};

// Main validation function
export const validateForm = (data: FormData, isEdit: boolean = false): ValidationErrors => {
  const errors: ValidationErrors = {};

  // Required field validation
  if (!data.first_name?.trim()) {
    errors.first_name = 'First name is required';
  } else if (!isValidName(data.first_name)) {
    errors.first_name = 'First name can only contain letters, spaces, and hyphens';
  }

  if (!data.last_name?.trim()) {
    errors.last_name = 'Last name is required';
  } else if (!isValidName(data.last_name)) {
    errors.last_name = 'Last name can only contain letters, spaces, and hyphens';
  }

  // Email validation (required for create, optional but validated for edit)
  if (!isEdit && !data.email?.trim()) {
    errors.email = 'Email is required';
  } else if (data.email?.trim() && !isValidEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Phone validation (required for create, optional but validated for edit)
  if (!isEdit && !data.phone?.trim()) {
    errors.phone = 'Phone number is required';
  } else if (data.phone?.trim() && !isValidPhoneNumber(data.phone)) {
    errors.phone = 'Please enter a valid 10-digit phone number';
  }

  // Date of birth validation
  const dobValidation = isValidDateOfBirth(data.date_of_birth);
  if (!dobValidation.isValid) {
    errors.date_of_birth = dobValidation.error;
  }

  // Address validation (required for create, optional but validated for edit)
  if (!isEdit && !data.address_street?.trim()) {
    errors.address_street = 'Street address is required';
  }

  if (!isEdit && !data.address_city?.trim()) {
    errors.address_city = 'City is required';
  } else if (data.address_city?.trim() && !isValidCity(data.address_city)) {
    errors.address_city = 'City can only contain letters, spaces, hyphens, and periods';
  }

  if (!isEdit && !data.address_state?.trim()) {
    errors.address_state = 'State is required';
  } else if (data.address_state?.trim() && !isValidState(data.address_state)) {
    errors.address_state = 'Please enter a valid 2-letter state code (e.g., NY)';
  }

  if (!isEdit && !data.address_postalCode?.trim()) {
    errors.address_postalCode = 'Zip code is required';
  } else if (data.address_postalCode?.trim() && !isValidZipCode(data.address_postalCode)) {
    errors.address_postalCode = 'Please enter a valid zip code (e.g., 12345 or 12345-6789)';
  }

  return errors;
};

// Helper function to clear specific validation error
export const clearValidationError = (
  errors: ValidationErrors,
  field: keyof ValidationErrors
): ValidationErrors => {
  const newErrors = { ...errors };
  delete newErrors[field];
  return newErrors;
};

// Helper function to get validation error for a field
export const getValidationError = (
  errors: ValidationErrors,
  field: keyof ValidationErrors
): string | undefined => {
  return errors[field];
};

// Helper function to check if form has any validation errors
export const hasValidationErrors = (errors: ValidationErrors): boolean => {
  return Object.keys(errors).length > 0;
};